import { Parse, UrlFormatter } from 'shared';

/**
 * Byte-identical port of the AngularJS `cnameUrl` filter
 * (account-v1/src/components/shared-features/cnameUrl.filter.js). Builds a
 * property's cname-rooted public URL: sorts the subdomains by
 * `isDefault` (custom first) → `routePurposeId` (specific first) → `createDate`
 * (most recent first), then composes `https://<host>[/<slug>][/<purpose>]` and
 * appends `params` to the query string via the native `UrlFormatter.format`.
 *
 * Extracted as a pure function (taking `fliptoHubDomain` instead of injecting
 * the AngularJS `environment`) so native consumers can drop the
 * `CNAME_URL_TOKEN` AngularJS-`$filter` bridge while staying character-for-
 * character identical to the frozen v1 baseline.
 *
 * NOTE: kept faithful to the v1 date tiebreaker (SIGNED `b - a` descending) —
 * not the `Math.abs(...)` variant in `cnameUrl.pipe.ts`, which is a latent bug.
 *
 * This logic is duplicated in the server-side `UnauthenticatedSubdomainService`
 * → `PrettyUrlFromCampaign`; keep any changes in sync (as the v1 filter noted).
 */
export enum CnameRoutePurpose {
  General = 0,
  PhotoExplorer = 1,
  Landing = 2,
  Invite = 3,
  Poststay = 4,
  Trip = 5,
}

function getPurposeAsText(purpose: number): string {
  switch (purpose) {
    case CnameRoutePurpose.PhotoExplorer:
      return 'photo-explorer';
    case CnameRoutePurpose.Landing:
      return 'landing';
    case CnameRoutePurpose.Invite:
      return 'invite';
    case CnameRoutePurpose.Poststay:
      return 'poststay';
    case CnameRoutePurpose.Trip:
      return 'trip';
    default:
      return '';
  }
}

function createUrl(host: string, slug: string | null, app: string | null, params: any): string {
  const segments = ['https://', host];

  if (slug) {
    segments.push('/');
    segments.push(slug);
  }

  if (app) {
    segments.push('/');
    segments.push(app);
  }

  const baseUrl = Array.prototype.join.call(segments, '');

  return UrlFormatter.format(baseUrl, params, true);
}

export function buildCnameUrl(
  domains: any,
  purpose: number,
  propertySlug: string,
  params: any,
  fliptoHubDomain: string
): string {
  const _domains = Parse.arrayOrDefault(domains);
  if (!_domains.length) {
    return '';
  }

  const sortedDomains = _domains.slice().sort((a: any, b: any) => {
    // order by default (custom first, default last)
    let x = a.isDefault - b.isDefault;
    if (x !== 0) {
      return x;
    }

    // Then by purpose descending (specific first, general last)
    x = b.routePurposeId - a.routePurposeId;
    if (x !== 0) {
      return x;
    }

    // Then by date descending (recent first, old last)
    return new Date(b.createDate).getTime() - new Date(a.createDate).getTime();
  });

  if (sortedDomains.length === 0) {
    return '';
  }

  const selectedDomain = sortedDomains[0];
  const properties = Parse.arrayOrDefault(selectedDomain.properties);
  const includeSlug = properties.length > 1;
  const includePurpose = selectedDomain.routePurpose !== purpose;

  return createUrl(
    Parse.bool(selectedDomain.isDefault) ? selectedDomain.host + '.' + fliptoHubDomain : selectedDomain.host,
    includeSlug ? propertySlug : null,
    includePurpose ? getPurposeAsText(purpose) : null,
    params
  );
}
